import { useEffect, useState } from 'react';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import useGetSidebarData from 'routes/RoutesData';
import useSidebarContext from 'context/CollapseSidebarContext';
import { Icon } from 'components/Base/Icon';
import { Menu } from 'components/Base/Layout/Sidebar/Menu';

type ItemType = NonNullable<MenuProps['items']>[number];

const LabelWithDataCy = ({
  label,
  dataCy,
}: {
  label: string;
  dataCy?: string;
}) => <div data-cy={dataCy}>{label}</div>;

const SidebarBodyMenu = () => {
  const { collapsed } = useSidebarContext();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation().pathname;
  const { privateRoutes } = useGetSidebarData();

  const routesInSidebar = privateRoutes.filter(route => route.show);
  const subRoutesInSidebar = routesInSidebar
    .map(route => route.subRoutes?.filter(subRoute => subRoute.show))
    .flat();

  const selectedKey =
    [...subRoutesInSidebar]
      .sort((a, b) => (b?.path?.length || 0) - (a?.path?.length || 0))
      .find(route => location.startsWith(`/app/${route?.path}`))?.path ||
    routesInSidebar.find(route => location.startsWith(`/app/${route.path}`))
      ?.path ||
    '';

  useEffect(() => {
    if (location) {
      setOpenKeys([selectedKey.split('/')[0]]);
    }
  }, [location]);

  const items: ItemType[] = routesInSidebar.map(item => {
    const subRoutesShown = (item?.subRoutes || [])?.filter(el => el.show);
    const hasSubRoutes = subRoutesShown?.length;
    const parentRouteIcon =
      selectedKey === item.path ||
      (openKeys.includes(item.path) && item.selectedIcon)
        ? item.selectedIcon
        : item.icon;

    const isIconTypeString = typeof parentRouteIcon === 'string';

    if (hasSubRoutes) {
      return {
        label: (
          <LabelWithDataCy label={item.title || ''} dataCy={item.dataCy} />
        ),
        key: item.path,
        icon:
          parentRouteIcon &&
          (isIconTypeString ? (
            <Icon icon={parentRouteIcon} />
          ) : (
            <div>{parentRouteIcon}</div>
          )),
        children: subRoutesShown?.map(subRoute => {
          const subRouteIcon =
            selectedKey === subRoute.path
              ? subRoute.selectedIcon
              : subRoute.icon;
          return {
            label: (
              <LabelWithDataCy
                label={subRoute.title || ''}
                dataCy={subRoute.dataCy}
              />
            ),
            key: subRoute.path,
            icon:
              subRouteIcon &&
              (typeof subRouteIcon === 'string' ? (
                <Icon icon={subRouteIcon} />
              ) : (
                <div>{subRouteIcon}</div>
              )),
            onClick: () => {
              navigate(subRoute.path);
            },
          };
        }),
      };
    }
    return {
      label: <LabelWithDataCy label={item.title || ''} dataCy={item.dataCy} />,
      key: item.path,
      icon:
        parentRouteIcon &&
        (isIconTypeString ? (
          <Icon icon={parentRouteIcon} />
        ) : (
          <div>{parentRouteIcon}</div>
        )),
      onClick: () => {
        navigate(item.path);
        setOpenKeys([]);
      },
    };
  });

  return (
    <Menu
      selectedKeys={[selectedKey]}
      mode="inline"
      items={items}
      $collapsed={collapsed}
      onOpenChange={keys => {
        const selectedKeyParent = selectedKey.split('/')[0];
        const lastOpenedKey = keys[keys.length - 1];
        if (keys.length > 1) {
          setOpenKeys([lastOpenedKey, selectedKeyParent]);
        } else {
          setOpenKeys(keys);
        }
      }}
      {...(openKeys.length && { openKeys })}
    />
  );
};

export default SidebarBodyMenu;
